import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TagInput } from "@/components/TagInput";
import { Plus, Pencil, Trash2, Building } from "lucide-react";

export const EntitesCatalogue = ({ canEdit }) => {
  const [entites, setEntites] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: "", description: "", services: [], site_internet: "", angle_commercial: "" });

  const load = () => api.get("/entites").then(({ data }) => setEntites(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ nom: "", description: "", services: [], site_internet: "", angle_commercial: "" }); setEditing("new"); };
  const openEdit = (e) => { setForm({ nom: e.nom, description: e.description, services: e.services, site_internet: e.site_internet || "", angle_commercial: e.angle_commercial || "" }); setEditing(e.id); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/entites", form);
      else await api.put(`/entites/${editing}`, form);
      toast.success("Catalogue mis à jour");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Retirer « ${e.nom} » du catalogue ?`)) return;
    try { await api.delete(`/entites/${e.id}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Card><CardContent className="space-y-4 p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Vos sociétés et leurs services. Ajoutez-en autant que nécessaire : chaque campagne prospecte pour l'une d'elles.</p>
        {canEdit && <Button size="sm" onClick={openNew} data-testid="add-entite-button"><Plus size={14} className="mr-1" /> Ajouter une société</Button>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2" data-testid="entites-list">
        {entites.map((e) => (
          <div key={e.id} className="rounded-md border p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="flex items-center gap-1.5 font-display font-semibold"><Building size={15} className="text-accent" /> {e.nom}</p>
                <p className="text-xs text-muted-foreground">{e.description}</p>
              </div>
              {canEdit && (
                <div className="flex">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`edit-entite-${e.id}`}><Pencil size={13} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(e)}><Trash2 size={13} className="text-destructive" /></Button>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(e.services || []).map((s) => <span key={s} className="rounded-full bg-secondary px-2 py-0.5 text-xs">{s}</span>)}
            </div>
            {e.site_internet && <p className="mt-1.5 text-xs text-accent">{e.site_internet}</p>}
            {e.angle_commercial && <p className="mt-1 text-xs italic text-muted-foreground">Angle : {e.angle_commercial}</p>}
          </div>
        ))}
        {entites.length === 0 && <p className="text-sm text-muted-foreground">Aucune société au catalogue.</p>}
      </div>

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent data-testid="entite-dialog">
            <DialogHeader><DialogTitle className="font-display">{editing === "new" ? "Nouvelle société" : "Modifier la société"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom</Label><Input className="mt-1" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="entite-name-input" /></div>
              <div><Label>Description</Label><Input className="mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="entite-desc-input" /></div>
              <div><Label>Services</Label><div className="mt-1"><TagInput value={form.services} onChange={(v) => setForm({ ...form, services: v })} testId="entite-services-input" /></div></div>
              <div><Label>Site internet (mentionné dans les e-mails)</Label><Input className="mt-1" placeholder="https://www.exemple.fr" value={form.site_internet} onChange={(e) => setForm({ ...form, site_internet: e.target.value })} data-testid="entite-website-input" /></div>
              <div><Label>Angle commercial des messages</Label><Input className="mt-1" placeholder="Ex : demander si l'entreprise a besoin de personnel…" value={form.angle_commercial} onChange={(e) => setForm({ ...form, angle_commercial: e.target.value })} data-testid="entite-angle-input" /></div>
              <Button onClick={save} className="w-full" data-testid="entite-save-button">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </CardContent></Card>
  );
};
